/**
 * <biosphere-product-form> — AJAX add-to-cart for the form rendered by
 * snippets/biosphere-product-form.liquid. Behaviour: reference/specs/product-form.md.
 *
 * The stale-variant invariant: the submit button disables the moment a variant change
 * starts and is restored only from the resolved server state, so an empty, stale or
 * unavailable variant id can never be POSTed.
 *
 * Successful adds publish `biosphere:cart:change` with the cart payload. The start of
 * an add is published as `biosphere:cart:adding`, for surfaces that react to the click
 * rather than its result.
 */
const ERROR_DISPLAY_MS = 10000;
const SUCCESS_DISPLAY_MS = 5000;
const REENABLE_DELAY_MS = 1000;

class BiosphereProductForm extends HTMLElement {
  #form;
  #idInput;
  #submit;
  #error;
  #live;
  #quantity;
  #abort;
  #messageTimer;
  // Blocks double-tap adds. A flag, not `disabled`: the variant flow resets that.
  #adding = false;

  connectedCallback() {
    this.#form = this.querySelector(".biosphere-product-form__form");
    this.#idInput = this.querySelector('input[name="id"]');
    this.#submit = this.querySelector(".biosphere-product-form__submit");
    this.#error = this.querySelector(".biosphere-product-form__error");
    this.#live = this.querySelector(".biosphere-product-form__live");
    this.#quantity = this.querySelector("biosphere-quantity");
    if (!this.#form || !this.#idInput || !this.#submit) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#form.addEventListener("submit", (event) => this.#onSubmit(event), {
      signal,
    });
    document.addEventListener(
      "biosphere:variant-picker:change",
      (event) => this.#onVariantChangeStart(event),
      { signal },
    );
    document.addEventListener(
      "biosphere:variant-picker:update",
      (event) => this.#onVariantUpdate(event),
      { signal },
    );
    document.addEventListener(
      "biosphere:cart:change",
      (event) => this.#onCartChange(event),
      { signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#messageTimer);
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#submit.disabled || this.#adding) return;

    // Max-quantity pre-check: never POST an add the rules already forbid.
    const check = this.#quantity?.canAdd?.();
    if (check && !check.canAdd) {
      const message = (this.dataset.errorMaxTemplate ?? "").replace(
        "[maximum]",
        String(check.max),
      );
      this.#showError(message);
      this.#submit.disabled = true;
      setTimeout(() => {
        this.#submit.disabled = false;
      }, REENABLE_DELAY_MS);
      return;
    }

    this.#adding = true;
    this.#form.setAttribute("aria-busy", "true");
    try {
      await this.#add();
    } finally {
      this.#adding = false;
      this.#form.removeAttribute("aria-busy");
    }
  }

  async #add() {
    // Published before the request, not after it: the cart drawer opens on
    // this so the buyer sees a response to their click straight away and
    // waits inside the drawer rather than in front of an unchanged page.
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:adding", {
        detail: { source: this.dataset.formId },
      }),
    );

    let payload;
    try {
      const response = await fetch(this.#form.action, {
        method: "POST",
        body: new FormData(this.#form),
        headers: { Accept: "application/json" },
      });
      payload = await response.json();
    } catch {
      this.#showError(this.dataset.errorNetwork ?? "");
      // The drawer opened on cart:adding and only cart:change releases it — see
      // #refreshCart. A failed add has to publish too, or it stays veiled.
      this.#publishCartChange(null, "update", null);
      return;
    }

    // The platform signals add errors (sold out mid-flight, inventory
    // exceeded) with a status field; its message is shown verbatim.
    if (payload.status) {
      this.#showError(payload.message ?? this.dataset.errorNetwork ?? "");
      await this.#refreshCart();
      return;
    }

    this.#hideError();
    this.#announce(this.dataset.addedMessage ?? "", SUCCESS_DISPLAY_MS);
    // The add response is the line the platform created. Passed on so subscribers know
    // which item was added, rather than inferring it from a cart that may have taken
    // another add in the meantime.
    await this.#refreshCart("add", payload);
  }

  /** Fetch cart state, publish it document-wide, and sync local displays. */
  async #refreshCart(action = "update", item = null) {
    let cart = null;
    try {
      const response = await fetch(`${this.dataset.cartUrl}.json`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      cart = await response.json();
    } catch {
      // Published anyway, with no cart. Surfaces went busy the moment the add began
      // and it is this event that releases them — returning quietly here left the
      // drawer veiled behind a spinner with nothing left to arrive. Every subscriber
      // already guards a missing cart, and none of them shows a figure without one.
      this.#publishCartChange(null, "update", null);
      return null;
    }
    this.#applyCartQuantity(cart);
    this.#publishCartChange(cart, action, item);
    return cart;
  }

  #publishCartChange(cart, action, item) {
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:change", {
        detail: { cart, action, item, source: this.dataset.formId },
      }),
    );
  }

  #applyCartQuantity(cart) {
    const variantId = Number.parseInt(this.#idInput.value, 10);
    const line = cart?.items?.find((item) => item.variant_id === variantId);
    const quantity = line?.quantity ?? 0;

    this.#quantity?.setCartQuantity?.(quantity);

    const count = this.querySelector(".biosphere-product-form__cart-count");
    if (count) {
      count.textContent = (count.dataset.template ?? "").replace(
        "[count]",
        String(quantity),
      );
      count.hidden = quantity === 0;
    }
  }

  #onCartChange(event) {
    const { cart, source } = event.detail ?? {};
    if (source === this.dataset.formId || !cart) return;
    this.#applyCartQuantity(cart);
  }

  #onVariantChangeStart(event) {
    if (event.detail?.productId !== this.dataset.productId) return;
    // Stale-variant invariant: no add may race an in-flight change.
    this.#submit.disabled = true;
  }

  #onVariantUpdate(event) {
    const {
      productId,
      variantId,
      available,
      document: fetched,
    } = event.detail ?? {};
    if (productId !== this.dataset.productId) return;

    this.#idInput.value = variantId ?? "";

    const fresh = fetched?.querySelector(
      `biosphere-product-form[data-product-id="${productId}"]`,
    );
    const freshSubmit = fresh?.querySelector(".biosphere-product-form__submit");

    if (freshSubmit) {
      this.#submit.disabled = freshSubmit.disabled;

      // The button's markup, not just its words. It holds slots — the verb and the price —
      // that other controls write into by selector, and textContent would flatten them
      // into a text node, leaving a button that reads correctly for this variant and can
      // never be updated again. Both sides come from the same storefront render.
      this.#submit.innerHTML = freshSubmit.innerHTML;
    } else {
      // No form in the response (e.g. the template does not render one
      // yet): fall back to resolved availability alone.
      this.#submit.disabled = !available;
    }

    const freshInput = fresh?.querySelector(".biosphere-quantity__input");
    if (freshInput && this.#quantity?.setConstraints) {
      this.#quantity.setConstraints(
        freshInput.getAttribute("min"),
        freshInput.getAttribute("max"),
        freshInput.getAttribute("step"),
      );
      this.#quantity.setCartQuantity(
        Number.parseInt(freshInput.dataset.cartQuantity ?? "", 10) || 0,
      );
    }

    if (fresh) {
      // The buyer's plan choice survives the re-render when the new
      // variant still offers that plan (spec: selling-plan.md).
      const previousPlan = this.querySelector(
        "[data-selling-plan-select]",
      )?.value;

      this.#syncPart(".biosphere-product-form__quantity-label", fresh);
      this.#syncPart(".biosphere-product-form__rules", fresh);
      this.#syncPart(".biosphere-quantity__per-item", fresh);
      this.#syncPart(".biosphere-quantity__volume", fresh);
      this.#syncPart(".biosphere-selling-plan", fresh);

      const planSelect = this.querySelector("[data-selling-plan-select]");
      if (planSelect && previousPlan !== undefined) {
        const stillOffered = [...planSelect.options].some(
          (option) => option.value === previousPlan,
        );
        if (stillOffered) planSelect.value = previousPlan;
      }
    }
  }

  /** Replace, remove, or adopt one server-rendered part from the fresh form. */
  #syncPart(selector, fresh) {
    const current = this.querySelector(selector);
    const next = fresh.querySelector(selector);
    if (current && next) {
      current.replaceWith(next.cloneNode(true));
    } else if (current && !next) {
      current.remove();
    } else if (!current && next) {
      const anchor = selector.startsWith(".biosphere-quantity")
        ? this.#quantity
        : this.#form;
      anchor?.append(next.cloneNode(true));
    }
  }

  #showError(message) {
    if (this.#error) {
      this.#error.textContent = message;
      this.#error.hidden = false;
    }
    this.#announce(message, ERROR_DISPLAY_MS);
  }

  #hideError() {
    if (this.#error) this.#error.hidden = true;
  }

  #announce(message, duration) {
    if (!this.#live) return;
    clearTimeout(this.#messageTimer);
    this.#live.textContent = message;
    this.#messageTimer = setTimeout(() => {
      this.#live.textContent = "";
      this.#hideError();
    }, duration);
  }
}

if (!customElements.get("biosphere-product-form")) {
  customElements.define("biosphere-product-form", BiosphereProductForm);
}
